import { Label } from '@/components/ui/label'
import { SelectControl, TextareaControl, TextControl } from '@/frontend/features/Fde/FormControls'
import type { ElementProperty, FdeCopy, OntologyPropertyDefinition } from '@/frontend/features/Fde/types'

interface Props {
  schema: OntologyPropertyDefinition[]
  values: ElementProperty[]
  copy: FdeCopy
  onChange: (values: ElementProperty[]) => void
}

export default function ElementPropertyFields({ schema, values, copy, onChange }: Props) {
  const visibleSchema = schema.filter((definition) => definition.key !== 'image_url')
  const groups = visibleSchema.reduce<Record<string, OntologyPropertyDefinition[]>>((result, definition) => {
    result[definition.group] ||= []
    result[definition.group].push(definition)
    return result
  }, {})

  function update(key: string, value: string | number | boolean) {
    onChange(values.map((property) => property.key === key ? { ...property, value } : property))
  }

  if (visibleSchema.length === 0) return null
  return <div className="space-y-4 rounded-lg border border-border bg-muted/15 p-4">
    <div><h3 className="text-sm font-semibold text-foreground">{copy.workspace.standard_properties}</h3><p className="mt-1 text-xs text-muted-foreground">{copy.workspace.standard_properties_description}</p></div>
    {Object.entries(groups).map(([group, definitions]) => <fieldset key={group} className="grid gap-3 sm:grid-cols-2">
      <legend className="col-span-full mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{copy.workspace[`property_group_${group}`] || group}</legend>
      {definitions.map((definition) => {
        const property = values.find((item) => item.key === definition.key)
        const value = property?.value ?? ''
        const shared = { key: definition.key, label: definition.label, value: String(value), required: definition.required }
        if (definition.data_type === 'long_text') return <TextareaControl {...shared} className="sm:col-span-2" rows={2} onChange={(event) => update(definition.key, event.target.value)} />
        if (definition.data_type === 'select') return <SelectControl key={definition.key} label={definition.label} value={String(value)} placeholder={copy.common.none} options={definition.options || []} onChange={(next) => update(definition.key, next)} />
        if (definition.data_type === 'boolean') return <SelectControl key={definition.key} label={definition.label} value={String(value)} placeholder={copy.common.none} options={[{ value: 'true', label: copy.common.yes }, { value: 'false', label: copy.common.no }]} onChange={(next) => update(definition.key, next)} />
        const inputType = ({ email: 'email', phone: 'tel', url: 'url', image_url: 'url', number: 'number', date: 'date' } as Record<string, string>)[definition.data_type] || 'text'
        return <div key={definition.key}>
          <TextControl {...shared} type={inputType} onChange={(event) => update(definition.key, event.target.value)} />
          {definition.description && <p className="mt-1 text-[10px] leading-4 text-muted-foreground">{definition.description}</p>}
          {definition.data_type === 'image_url' && value && <div className="mt-2 flex items-center gap-2"><img src={String(value)} alt="" className="size-9 rounded-full border border-border object-cover" /><Label className="text-[10px] text-muted-foreground">{copy.workspace.image_preview}</Label></div>}
        </div>
      })}
    </fieldset>)}
  </div>
}
